// useDetectorFilters.tsx
import { useMemo, useState } from 'react';
import { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { GroundingConfigurationSchema } from '@ais-ui/api-spec/grounding';
import { DetectorFilters, DetectorSortConfig } from './types';


type CombinedDetector = EventdetectorDefinition | GroundingConfigurationSchema;

// Type guard to check if it's a GroundingConfigurationSchema
const isGroundingDetector = (
  detector: CombinedDetector
): detector is GroundingConfigurationSchema => {
  return 'createdBy' in detector && 'uuid' in detector;
};

// Helper to get owner from either detector type
const getDetectorOwner = (detector: CombinedDetector): string => {
  if (isGroundingDetector(detector)) {
    return detector.createdBy || '';
  }
  return detector.owner || '';
};

export const useDetectorFilters = (
  detectors: EventdetectorDefinition[],
  groundingDetectors: GroundingConfigurationSchema[],
  searchTerm: string
) => {
  const [detectorFilters, setDetectorFilters] = useState<DetectorFilters>({
    owner: 'all',
    type: 'all',
    severity: 'all',
  });

  const [sortConfig, setSortConfig] = useState<DetectorSortConfig>({
    type: 'name',
    direction: 'asc',
  });

  const filteredAndSortedDetectors = useMemo(() => {
    // Combine both detector types
    const combinedDetectors: CombinedDetector[] = [
      ...detectors,
      ...groundingDetectors,
    ];

    if (!combinedDetectors || combinedDetectors.length === 0) return [];

    // First apply search filter
    let result = [...combinedDetectors];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((detector) => {
        const name = detector.name?.toLowerCase() || '';
        const owner = getDetectorOwner(detector).toLowerCase();
        return name.includes(searchLower) || owner.includes(searchLower);
      });
    }

    // Apply filters
    result = result.filter((detector) => {
      // Filter by owner
      if (detectorFilters.owner !== 'all') {
        const detectorOwner = getDetectorOwner(detector);
        if (detectorOwner !== detectorFilters.owner) return false;
      }

      // Filter by type
      if (detectorFilters.type !== 'all') {
        const detectorType = detector.type || 'unknown';
        if (detectorType.toLowerCase() !== detectorFilters.type.toLowerCase())
          return false;
      }

      // Filter by severity
      if (detectorFilters.severity !== 'all') {
        const detectorSeverity = detector.severity?.toLowerCase() || 'note';
        if (detectorSeverity !== detectorFilters.severity) return false;
      }

      return true;
    });

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortConfig.type) {
        case 'name':
          const nameA = a.name || '';
          const nameB = b.name || '';
          comparison = nameA.localeCompare(nameB);
          break;

        case 'type':
          const typeA = a.type || 'unknown';
          const typeB = b.type || 'unknown';
          comparison = typeA.localeCompare(typeB);
          // Secondary sort by name if types are equal
          if (comparison === 0) {
            const nameA = a.name || '';
            const nameB = b.name || '';
            comparison = nameA.localeCompare(nameB);
          }
          break;

        case 'date':
          // Use createdAt for GroundingConfigurationSchema, fall back to name sort for EventdetectorDefinition
          const dateA =
            isGroundingDetector(a) && a.createdAt
              ? new Date(a.createdAt).getTime()
              : 0;
          const dateB =
            isGroundingDetector(b) && b.createdAt
              ? new Date(b.createdAt).getTime()
              : 0;

          if (dateA === 0 && dateB === 0) {
            // Neither has a date, sort by name instead
            const nameA = a.name || '';
            const nameB = b.name || '';
            comparison = nameA.localeCompare(nameB);
          } else {
            comparison = dateA - dateB;
          }
          break;
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [detectors, groundingDetectors, searchTerm, detectorFilters, sortConfig]);

  return {
    filteredAndSortedDetectors,
    detectorFilters,
    setDetectorFilters,
    sortConfig,
    setSortConfig,
  };
};
